import { useEffect, useMemo } from 'react';
import cx from 'classnames';
import { useSelector } from 'react-redux';
import { CircularLoadingSpinner } from 'lib/CircularLoadingSpinner/CircularLoadingSpinner';
import AllocationReport from 'components/common/AllocationReport';
import {
  CampaignPlanFiltersContext,
  CampaignPlanFiltersContextValue,
} from 'components/common/CampaignPlanFiltersContext';
import { CampaignPlanErrorTitle } from 'components/common/types/CampaignPlan.types';
import { useAppAuth } from 'components/common/App/AppAuthContext';
import { getAllocationReportConfig, getIsMapIncluded, getTouchedPoints } from 'store/sharedCampaignPlan/selectors';
import CampaignPlanMap from './CampaignPlanMap/CampaignPlanMap';
import PublicCampaignPlanContent from './PublicCampaignPlanContent';
import CampaignPlanMapFilters from './CampaignPlanMapFilters';
import { useCampaignPlanFilters } from './CampaignPlanMapFilters/useCampaignPlanFilters';
import { usePublicCampaignPlan } from './usePublicCampaignPlan';
import { Header } from './Header/Header';
import { LoadError } from './LoadError';
import { PlannerDataContext } from '../Planner/context/PlannerDataContext';
import { useDirectSalesPlannerData } from '../Planner/containers/DirectSalesPlanner/useDirectSalesPlannerData';
import { getCampaignPlanDisplayName } from './utils/getCampaignPlanDisplayName';
import { getAllocationReportPlannedMetricOptions } from './utils/getAllocationReportMetricOptions';

const PublicCampaignPlanPage: React.FC = () => {
  const {
    allocationReportDealLines,
    isAllocationReportSelectAllDefault,
    isAllocationReportSelectAllLocked,
    isSelectHidden,
    campaignPlanDetails,
    campaignPlanError,
    isLoading,
    localeCode,
    currencyCode,
    token,
  } = usePublicCampaignPlan();
  const isMapIncluded = useSelector(getIsMapIncluded);
  const touchedPoints = useSelector(getTouchedPoints);
  const plannerData = useDirectSalesPlannerData();
  const allocationReportConfig = useSelector(getAllocationReportConfig);

  const allocationReportPlannerMetricOptions = useMemo(
    () => getAllocationReportPlannedMetricOptions(allocationReportConfig),
    [allocationReportConfig],
  );

  const filters = useCampaignPlanFilters(token);

  const filtersContextValue = useMemo<CampaignPlanFiltersContextValue>(
    () => ({
      activeColorFilter: filters.activeColorFilter,
      selectedPois: filters.selectedPoiData,
      touchedPoints,
    }),
    [filters.activeColorFilter, filters.selectedPoiData, touchedPoints],
  );

  useEffect(() => {
    const previousTitle = document.title;

    if (campaignPlanDetails) {
      document.title = getCampaignPlanDisplayName({
        advertiserName: campaignPlanDetails.advertiserName,
        brandName: campaignPlanDetails.brandName,
      });
    }

    return () => {
      document.title = previousTitle;
    };
  }, [campaignPlanDetails]);

  const isRefreshVisible = campaignPlanError?.title !== CampaignPlanErrorTitle.UNAUTHORIZED;

  return (
    <div className="body-base font-inter h-screen flex flex-col justify-between overflow-y-auto bg-white">
      <Header />
      <main
        role="main"
        className={cx({
          'h-screen flex items-center justify-center': campaignPlanError,
          'px-7 md:px-14 flex-1': !campaignPlanError,
        })}
      >
        {campaignPlanError ? (
          <LoadError
            message={campaignPlanError?.detail}
            isRefreshVisible={isRefreshVisible}
            onRefreshClick={() => window.location.reload()}
          />
        ) : (
          <>
            {isLoading && <CircularLoadingSpinner />}
            {!isLoading && campaignPlanDetails && allocationReportDealLines.length > 0 && (
              <>
                <div className="max-w-7xl mx-auto md:px-7">
                  <PublicCampaignPlanContent campaignPlanDetails={campaignPlanDetails} publicLocaleCode={localeCode} />
                  <div className="p-4 border rounded-lg bg-neutral-100">
                    <div className="header-base mb-4">Allocation report</div>
                    <div className="bg-white rounded-lg overflow-hidden">
                      <AllocationReport
                        dealLines={allocationReportDealLines}
                        defaultSelectedLine={allocationReportDealLines[0]}
                        isDirectSales
                        publicToken={token}
                        publicLocaleCode={localeCode}
                        availableMetricOptions={allocationReportPlannerMetricOptions}
                        availableGroupByOptions={allocationReportConfig?.allowedGroupByOptions}
                        isSelectAllDefault={isAllocationReportSelectAllDefault}
                        isSelectAllLocked={isAllocationReportSelectAllLocked}
                        isSelectHidden={isSelectHidden}
                      />
                    </div>
                  </div>
                </div>
                {isMapIncluded && (
                  <div className="p-4 border rounded-lg mt-8 mb-10">
                    <CampaignPlanFiltersContext.Provider value={filtersContextValue}>
                      <CampaignPlanMapFilters filters={filters} />
                      <PlannerDataContext.Provider value={plannerData}>
                        <div className="h-180 w-full">
                          <CampaignPlanMap localeCode={localeCode} currencyCode={currencyCode} />
                        </div>
                      </PlannerDataContext.Provider>
                    </CampaignPlanFiltersContext.Provider>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
};

const PublicCampaignPlan: React.FC = () => {
  const { isAuthChecked } = useAppAuth();

  if (!isAuthChecked) {
    return (
      <div className="font-inter h-screen flex items-center justify-center bg-white" role="status" aria-live="polite">
        <CircularLoadingSpinner />
      </div>
    );
  }

  return <PublicCampaignPlanPage />;
};

export default PublicCampaignPlan;
